'use client';

import React, { useState } from 'react';
import { CheckCircle2, AlertTriangle, Unlink, RefreshCw, ExternalLink } from 'lucide-react';

type ConnectionStatus = 'connected' | 'syncing' | 'error';

const STATUS_CONFIG: Record<ConnectionStatus, { label: string; color: string; bg: string; border: string; icon: React.ReactNode }> = {
  connected: {
    label: 'Connected',
    color: 'text-emerald-700',
    bg: 'bg-emerald-50',
    border: 'border-emerald-200',
    icon: <CheckCircle2 size={13} className="text-emerald-600" />,
  },
  syncing: {
    label: 'Syncing…',
    color: 'text-amber-700',
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    icon: <RefreshCw size={13} className="text-amber-600 animate-spin" />,
  },
  error: {
    label: 'Sync Error',
    color: 'text-red-700',
    bg: 'bg-red-50',
    border: 'border-red-200',
    icon: <AlertTriangle size={13} className="text-red-600" />,
  },
};

export default function StravaConnectionCard() {
  const [status, setStatus] = useState<ConnectionStatus>('connected');
  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false);
  const [lastSync] = useState('Today at 9:14 AM');

  const cfg = STATUS_CONFIG[status];

  function handleSync() {
    setStatus('syncing');
    setTimeout(() => setStatus('connected'), 2500);
  }

  function handleDisconnect() {
    setShowDisconnectConfirm(false);
    // In a real app: revoke token and redirect to login
    alert('Strava account disconnected. You would be redirected to the login screen.');
  }

  return (
    <div className="rounded-2xl border border-[hsl(var(--border))] bg-white overflow-hidden">
      {/* Card header */}
      <div className="px-5 py-4 border-b border-[hsl(var(--border))] flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          {/* Strava orange logo mark */}
          <div className="w-8 h-8 rounded-lg bg-[#FC4C02] flex items-center justify-center shrink-0">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="white" xmlns="http://www.w3.org/2000/svg">
              <path d="M15.387 17.944l-2.089-4.116h-3.065L15.387 24l5.15-10.172h-3.066m-7.008-5.599l2.836 5.598h4.172L10.463 0l-7 13.828h4.169" />
            </svg>
          </div>
          <div>
            <p className="text-sm font-semibold text-[hsl(var(--foreground))]">Strava Account</p>
            <p className="text-[11px] text-[hsl(var(--muted-foreground))]">marcus.tanner@strava.com</p>
          </div>
        </div>
        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold border ${cfg.bg} ${cfg.border} ${cfg.color}`}>
          {cfg.icon}
          {cfg.label}
        </span>
      </div>

      {/* Sync info */}
      <div className="px-5 py-4 space-y-3">
        <div className="flex items-center justify-between text-sm">
          <span className="text-[hsl(var(--muted-foreground))]">Last synced</span>
          <span className="font-medium text-[hsl(var(--foreground))]">{lastSync}</span>
        </div>
        <div className="flex items-center justify-between text-sm">
          <span className="text-[hsl(var(--muted-foreground))]">Activities imported</span>
          <span className="font-medium text-[hsl(var(--foreground))]">142 rides</span>
        </div>
        <div className="flex items-center justify-between text-sm">
          <span className="text-[hsl(var(--muted-foreground))]">Segments analysed</span>
          <span className="font-medium text-[hsl(var(--foreground))]">1,847</span>
        </div>
      </div>

      {/* Actions */}
      <div className="px-5 py-4 border-t border-[hsl(var(--border))] flex flex-wrap items-center gap-2">
        <button
          onClick={handleSync}
          disabled={status === 'syncing'}
          className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-xs font-semibold bg-[hsl(var(--primary))] text-white hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity"
        >
          <RefreshCw size={12} className={status === 'syncing' ? 'animate-spin' : ''} />
          {status === 'syncing' ? 'Syncing…' : 'Sync Now'}
        </button>
        <a
          href="https://www.strava.com/athlete/profile"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-xs font-semibold border border-[hsl(var(--border))] text-[hsl(var(--foreground))] hover:bg-[hsl(var(--muted))] transition-colors"
        >
          <ExternalLink size={12} />
          View on Strava
        </a>
        <button
          onClick={() => setShowDisconnectConfirm(true)}
          className="ml-auto inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-xs font-semibold text-red-600 border border-red-200 hover:bg-red-50 transition-colors"
        >
          <Unlink size={12} />
          Disconnect
        </button>
      </div>

      {/* Disconnect confirmation */}
      {showDisconnectConfirm && (
        <div className="mx-5 mb-5 p-4 rounded-xl bg-red-50 border border-red-200">
          <p className="text-sm font-semibold text-red-800 mb-1">Disconnect Strava?</p>
          <p className="text-xs text-red-700 mb-3">
            This will remove access to your Strava data. All locally cached segments and KOM data will be cleared.
          </p>
          <div className="flex gap-2">
            <button
              onClick={handleDisconnect}
              className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-red-600 text-white hover:bg-red-700 transition-colors"
            >
              Yes, disconnect
            </button>
            <button
              onClick={() => setShowDisconnectConfirm(false)}
              className="px-3 py-1.5 rounded-lg text-xs font-semibold border border-red-200 text-red-700 hover:bg-red-100 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
